// src/pages/FaqPage.js
import React, { useEffect, useState } from 'react'
import SidebarLayout from '../components/SidebarLayout'

async function loadFAQSection(fileName) {
  try {
    const response = await fetch(`/faq/${fileName}`)

    if (!response.ok) {
      throw new Error(`Failed to load ${fileName}`)
    }

    const text = await response.text()
    const entries = []
    const blocks = text.split(/\n(?=Q:)/g)

    for (const block of blocks) {
      const qMatch = block.match(/^Q:\s*(.+)/m)
      const aMatch = block.match(/^A:\s*([\s\S]*)/m)

      if (qMatch && aMatch) {
        entries.push({
          question: qMatch[1].trim(),
          answer: aMatch[1].trim(),
        })
      }
    }

    return entries
  } catch (error) {
    console.error(`Error loading FAQ section "${fileName}":`, error)
    return []
  }
}

const FAQ_SECTIONS = [
  {
    key: 'about',
    title: 'About CuvrIQ',
    fileName: 'section1-about-CuvrIQ.txt',
  },
  {
    key: 'riq',
    title: 'Using Riq',
    fileName: 'section2-using-riq.txt',
  },
  {
    key: 'plans',
    title: 'Viewing Plan Details',
    fileName: 'section3-viewing-plans.txt',
  },
  {
    key: 'general',
    title: 'General Questions',
    fileName: 'section4-general.txt',
  },
]

const VIDEO_LINKS = [
  {
    title: 'How to Upload a Benefit Breakdown',
    description:
      'See how to upload a document, review the extracted data, and move it into the verification workflow.',
    url: 'https://youtu.be/nnl751lnVvM',
  },
  {
    title: 'How to Use Riq',
    description:
      'Learn how to ask coverage questions in natural language and get fast, easy-to-understand answers.',
    url: 'https://youtu.be/_-2c9JUdDyM',
  },
  {
    title: 'How to View All Plans',
    description:
      'Browse verified plans, review plan details, and understand how to find the information you need.',
    url: 'https://youtu.be/7Yw_FynqcPg',
  },
  {
    title: 'How to Submit Feedback',
    description:
      'Learn how to share issues, suggestions, or ideas so the platform can keep improving.',
    url: 'https://youtu.be/Ue9178JW2zs',
  },
]

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '14px',
        marginBottom: '0.9rem',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: '1rem 1.1rem',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          fontSize: '1rem',
          fontWeight: 600,
          color: '#0f172a',
        }}
      >
        <span>{item.question}</span>
        <span
          style={{
            fontSize: '1.25rem',
            lineHeight: 1,
            color: '#0891b2',
            flexShrink: 0,
          }}
        >
          {isOpen ? '−' : '+'}
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            padding: '0 1.1rem 1.1rem',
            color: '#334155',
            fontSize: '0.98rem',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          {item.answer}
        </div>
      )}
    </div>
  )
}

function SectionCard({ title, children }) {
  return (
    <section
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '18px',
        padding: '1.25rem',
        marginBottom: '1.5rem',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
      }}
    >
      <h2
        style={{
          margin: '0 0 1rem 0',
          fontSize: '1.2rem',
          fontWeight: 700,
          color: '#0f172a',
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  )
}

function FaqPage() {
  const [faqData, setFaqData] = useState({})
  const [openItems, setOpenItems] = useState({})

  useEffect(() => {
    async function loadAllSections() {
      const results = await Promise.all(
        FAQ_SECTIONS.map(async (section) => {
          const entries = await loadFAQSection(section.fileName)
          return [section.key, entries]
        })
      )

      setFaqData(Object.fromEntries(results))
    }

    loadAllSections()
  }, [])

  const toggleItem = (sectionKey, index) => {
    const itemKey = `${sectionKey}-${index}`
    setOpenItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }))
  }

  return (
    <SidebarLayout>
      <div
        style={{
          minHeight: '100%',
          backgroundColor: '#f8fafc',
          padding: '1.5rem',
        }}
      >
        <div
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
          }}
        >
          <div
            style={{
              background:
                'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #155e75 100%)',
              color: '#ffffff',
              borderRadius: '24px',
              padding: '2rem 1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
            }}
          >
            <div
              style={{
                fontSize: '1.9rem',
                fontWeight: 800,
                marginBottom: '0.75rem',
              }}
            >
              Frequently Asked Questions
            </div>

            <div
              style={{
                maxWidth: '860px',
                fontSize: '1rem',
                lineHeight: 1.7,
                color: '#e2e8f0',
              }}
            >
              Welcome to the CuvrIQ FAQ page. Many questions can also be answered
              directly through Riq, but this page gives you a clear place to
              understand what CuvrIQ is, what it is not, how to use Riq, how to
              select or switch plans, and where to find quick walkthrough videos.
            </div>
          </div>

          <SectionCard title="Quick Video Walkthroughs">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                gap: '1rem',
              }}
            >
              {VIDEO_LINKS.map((video) => (
                <a
                  key={video.title}
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    textDecoration: 'none',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    padding: '1rem',
                    display: 'block',
                    color: '#0f172a',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.98rem',
                      fontWeight: 700,
                      marginBottom: '0.45rem',
                    }}
                  >
                    {video.title}
                  </div>
                  <div
                    style={{
                      fontSize: '0.92rem',
                      lineHeight: 1.6,
                      color: '#475569',
                      marginBottom: '0.8rem',
                    }}
                  >
                    {video.description}
                  </div>
                  <div
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: '#0891b2',
                    }}
                  >
                    Watch Video →
                  </div>
                </a>
              ))}
            </div>
          </SectionCard>

          {FAQ_SECTIONS.map((section) => {
            const items = faqData[section.key] || []

            if (!items.length) {
              return null
            }

            return (
              <SectionCard key={section.key} title={section.title}>
                {items.map((item, index) => (
                  <FaqItem
                    key={`${section.key}-${index}`}
                    item={item}
                    isOpen={!!openItems[`${section.key}-${index}`]}
                    onToggle={() => toggleItem(section.key, index)}
                  />
                ))}
              </SectionCard>
            )
          })}

          <SectionCard title="Still Have a Question?">
            <div
              style={{
                color: '#334155',
                fontSize: '0.98rem',
                lineHeight: 1.7,
              }}
            >
              You can also go to the <strong>Ask Riq</strong> page from the
              sidebar and ask your question directly in natural language. For
              example, you can ask about coverage, plan limitations, waiting
              periods, or how to switch to another plan during the conversation.
            </div>
          </SectionCard>
        </div>
      </div>
    </SidebarLayout>
  )
}

export default FaqPage// src/pages/FaqPage.js
